"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.contentChangeSchema = exports.RenderingConfigSchema = exports.RenderingComponentTypeSchema = exports.DefaultPipedreamNodeResultSchema = exports.initializeToolDefinitions = exports.TOOL_DEFINITIONS = exports.getToolsByCategoryMap = exports.getAllIcons = exports.isValidIcon = exports.isValidToolId = exports.getAllToolMetadata = exports.getAllToolIds = exports.getToolsByCategory = exports.getToolMetadata = exports.createBgColor = exports.BRAND_COLORS = exports.WorkflowToolIds = exports.WORKFLOW_TOOL_IDS = exports.ToolIconSchema = exports.ToolFailureReasonSchema = exports.ToolCategorySchema = exports.emitModeSchema = void 0;
exports.createToolMetadata = createToolMetadata;
exports.createEmitModeToolMetadata = createEmitModeToolMetadata;
exports.getToolResultSchema = getToolResultSchema;
exports.unfoldDynamicResultSchemas = unfoldDynamicResultSchemas;
exports.decorateDynamicSchemas = decorateDynamicSchemas;
exports.toDynamicSchemas = toDynamicSchemas;
exports.defineDynamicSchemas = defineDynamicSchemas;
exports.addEmitMode = addEmitMode;
exports.validateDependenciesAreEnumOrBoolean = validateDependenciesAreEnumOrBoolean;
exports.PipedreamNodeArgsSchema = PipedreamNodeArgsSchema;
exports.PipedreamNodeResultSchema = PipedreamNodeResultSchema;
exports.GoogleAdsNodeArgsSchema = GoogleAdsNodeArgsSchema;
exports.GoogleAdsNodeResultSchema = GoogleAdsNodeResultSchema;
var zod_1 = require("zod");
// Emit mode schema (shared)
exports.emitModeSchema = zod_1.z
    .enum(['multi', 'single'])
    .describe('required: Emit mode for the tool.');
// Tool category for organization and styling
exports.ToolCategorySchema = zod_1.z.enum([
    'native',
    'ads',
    'integration',
    'ai',
    'search',
    'seo',
    'social',
    'communication',
]);
// Tool failure reasons for structured error handling
exports.ToolFailureReasonSchema = zod_1.z.enum([
    'insufficient_credits',
    'no_data_found',
    'rate_limited',
    'invalid_parameters',
    'external_service_unavailable',
    'quota_exceeded',
    'authentication_failed',
    'dependency_failed',
    'cancelled',
    'api_error',
    'missing_configuration',
    'execution_error',
]);
// Available icon identifiers (must match what's available in frontend)
exports.ToolIconSchema = zod_1.z.enum([
    // Ads
    'google_ads',
    // Search & Web
    'search',
    'browser',
    // Communication & Social
    'gmail',
    'google_drive',
    'linkedin',
    'meta',
    'reddit',
    'twitter',
    'youtube',
    'wordpress',
    // Native/System
    'approval',
    'upload',
    'batch',
    'flatten',
    'filter',
    'merge',
    'route',
    'target',
    // CSV & Data
    'csv',
    'ai-csv',
    // AI/LLM
    'image-gen',
    'chat',
    'text-gen',
    'text-edit',
    // Framer
    'framer',
    // Webflow
    'webflow',
    // Shopify
    'shopify',
    // Fallbacks
    'default',
]);
function createToolMetadata(metadata, result) {
    if (result instanceof zod_1.z.ZodType) {
        return __assign(__assign({}, metadata), { resultSchema: toDynamicSchemas(result) });
    }
    return __assign(__assign({}, metadata), { resultSchema: result });
}
function createEmitModeToolMetadata(metadata, result) {
    if (result instanceof zod_1.z.ZodType) {
        return __assign(__assign({}, metadata), { resultSchema: addEmitMode(toDynamicSchemas(result)) });
    }
    return __assign(__assign({}, metadata), { resultSchema: addEmitMode(result) });
}
// Helper to get nested property value using dot notation
function getNestedValue(obj, path) {
    return path.split('.').reduce(function (current, key) { return current === null || current === void 0 ? void 0 : current[key]; }, obj);
}
// Helper to check if two condition objects match
function conditionsMatch(args, conditions) {
    return Object.entries(conditions).every(function (_a) {
        var key = _a[0], value = _a[1];
        // Support nested property access with dot notation
        var actualValue = key.includes('.') ? getNestedValue(args, key) : args[key];
        return actualValue === value;
    });
}
// Helper to get the result schema from any tool metadata
function getToolResultSchema(tool, args) {
    var dynamicSchema = tool.resultSchema;
    // Resolve default values from the Zod schema
    var resolvedArgs = __assign({}, args);
    // Handle emitMode default for tools that use createEmitModeToolMetadata
    if (tool.argsSchema && 'shape' in tool.argsSchema) {
        var shape = tool.argsSchema.shape;
        if ('emitMode' in shape && resolvedArgs.emitMode === undefined) {
            var emitModeField = shape.emitMode;
            if (emitModeField && typeof emitModeField === 'object' && 'defaultValue' in emitModeField) {
                var defaultValue = emitModeField.defaultValue;
                if (typeof defaultValue === 'function') {
                    resolvedArgs.emitMode = defaultValue();
                }
            }
        }
    }
    // Find the matching variant
    for (var _i = 0, _a = dynamicSchema.variants; _i < _a.length; _i++) {
        var variant = _a[_i];
        if (conditionsMatch(resolvedArgs, variant.when)) {
            return variant.schema;
        }
    }
    // No variant matched, use default or throw error
    if (dynamicSchema.defaultSchema) {
        return dynamicSchema.defaultSchema;
    }
    throw new Error("No matching result schema variant found for tool ".concat(tool.id, " with args: ").concat(JSON.stringify(resolvedArgs)));
}
// Helper to unfold dynamic result schemas for LLM context
function unfoldDynamicResultSchemas(dynamicSchema) {
    var results = dynamicSchema.variants.map(function (variant) { return ({
        condition: variant.description ||
            "when ".concat(Object.entries(variant.when)
                .map(function (_a) {
                var k = _a[0], v = _a[1];
                return "".concat(k, "=\"").concat(v, "\"");
            })
                .join(' and ')),
        resultSchema: variant.schema,
    }); });
    // Add default if it exists
    if (dynamicSchema.defaultSchema) {
        results.push({
            condition: 'default',
            resultSchema: dynamicSchema.defaultSchema,
        });
    }
    return results;
}
// Generic decorator helper to transform dynamic schemas in a consistent way
function decorateDynamicSchemas(base, options) {
    var dynamic = base;
    var sourceVariants = dynamic.variants.length === 0
        ? [
            {
                when: {},
                schema: dynamic.defaultSchema,
            },
        ]
        : dynamic.variants; // safe cast to reuse shape
    var transformedVariants = sourceVariants.flatMap(function (variant) {
        return options.makeVariants(variant);
    });
    return {
        dependencies: __spreadArray(__spreadArray([], dynamic.dependencies, true), [options.dependency], false),
        variants: transformedVariants,
        defaultSchema: options.transformDefault(dynamic.defaultSchema),
    };
}
function toDynamicSchemas(base) {
    return {
        dependencies: [],
        variants: [],
        defaultSchema: base,
    };
}
// Strongly-typed factory to define dynamic result schemas ensuring variant/default are assignable to TResult
function defineDynamicSchemas() {
    return function (config) {
        return config;
    };
}
// Decorator: add emitMode handling to any base (static or dynamic). EmitMode 'multi' will return an array of results, 'single' will return a single result.
function addEmitMode(base) {
    return decorateDynamicSchemas(base, {
        dependency: 'emitMode',
        makeVariants: function (variant) { return [
            {
                when: __assign(__assign({}, variant.when), { emitMode: 'single' }),
                schema: variant.schema,
                description: variant.description ? "".concat(variant.description, " (single result)") : undefined,
            },
            {
                when: __assign(__assign({}, variant.when), { emitMode: 'multi' }),
                schema: zod_1.z.array(variant.schema),
                description: variant.description ? "".concat(variant.description, " (array of results)") : undefined,
            },
        ]; },
        // Keep runtime default as the original single schema, but widen the type to include array
        transformDefault: function (defaultSchema) {
            return defaultSchema;
        },
    });
}
function getChildShape(schema) {
    // _def.shape() returns the current shape map
    return schema._def.shape();
}
function getTypeAtPath(schema, path) {
    var _a, _b;
    var current = schema;
    for (var i = 0; i < path.length; i += 1) {
        var key = path[i];
        if (current instanceof zod_1.ZodObject) {
            var shape = getChildShape(current);
            var next = shape[key];
            if (!next)
                return null;
            current = next;
            continue;
        }
        if (current instanceof zod_1.ZodDiscriminatedUnion) {
            var disc = (_a = current.discriminator) !== null && _a !== void 0 ? _a : (_b = current._def) === null || _b === void 0 ? void 0 : _b.discriminator;
            if (key === disc) {
                // Discriminant is always a literal; treat as enum-like
                return { kind: 'discriminant' };
            }
            // Cannot traverse other keys inside discriminated union in a simple way here
            return null;
        }
        // Unsupported traversal for non-object types
        return null;
    }
    return { kind: 'schema', schema: current };
}
function isEnumOrBooleanType(resolved) {
    if (resolved.kind === 'discriminant')
        return true;
    var t = resolved.schema;
    return t instanceof zod_1.ZodBoolean || t instanceof zod_1.ZodEnum || t instanceof zod_1.ZodLiteral;
}
function validateDependenciesAreEnumOrBoolean(argsSchema, dependencies) {
    for (var _i = 0, dependencies_1 = dependencies; _i < dependencies_1.length; _i++) {
        var dep = dependencies_1[_i];
        var parts = dep.split('.');
        var found = getTypeAtPath(argsSchema, parts);
        if (!found || !isEnumOrBooleanType(found)) {
            console.warn("Result schema dependency \"".concat(dep, "\" should reference a boolean, enum, or discriminant literal field."));
        }
    }
}
// All available tool IDs (automatically generated from tool definition files)
// To update this list, run: npm run generate-tool-ids
// This will scan all tool files and extract their IDs automatically
exports.WORKFLOW_TOOL_IDS = [
    'aiVisibility',
    'appStoreMetadata',
    'appStoreReviews',
    'facebookAdsAdManagement',
    'facebookAdsAdSetManagement',
    'facebookAdsCampaignManagement',
    'facebookAdsCreateCustomAudience',
    'facebookAdsCreateLookalikeAudience',
    'facebookAdsGetAdDetails',
    'facebookAdsGetAdSetDetails',
    'facebookAdsGetCampaignDetails',
    'facebookAdsGetCreative',
    'facebookAdsInsights',
    'facebookAdsLeadgenFormList',
    'facebookAdsLeadgenFormManagement',
    'facebookAdsListAccounts',
    'facebookAdsListAudiences',
    'facebookAdsPixelList',
    'facebookAdsPixelManagement',
    'facebookAdsSearchTargeting',
    'facebookAdsUpdateCustomAudience',
    'fetchFromUrl',
    'framerActions',
    'framerWebPublisher',
    'gmailAddLabelToEmail',
    'gmailArchiveEmail',
    'gmailCreateDraft',
    'gmailDownloadAttachment',
    'gmailFindEmail',
    'gmailListLabels',
    'gmailRemoveLabelFromEmail',
    'gmailSendEmail',
    'gmailUpdateOrgSignature',
    'gmailUpdatePrimarySignature',
    'googleAdsAdGroupManagement',
    'googleAdsAdManagement',
    'googleAdsAddKeywords',
    'googleAdsAssetGroupManagement',
    'googleAdsCalloutManagement',
    'googleAdsCampaignManagement',
    'googleAdsFetch',
    'googleAdsGetAdDetails',
    'googleAdsGetAdGroupDetails',
    'googleAdsGetAssetDetails',
    'googleAdsGetAssetGroupDetails',
    'googleAdsGetCampaignDetails',
    'googleAdsGetExtensionDetails',
    'googleAdsGetKeywordDetails',
    'googleAdsGetPlacementDetails',
    'googleAdsGetProductDetails',
    'googleAdsGetProductGroupDetails',
    'googleAdsGetSearchQueryDetails',
    'googleAdsInsights',
    'googleAdsListCampaigns',
    'googleAdsPauseKeywords',
    'googleAdsRemoveKeywords',
    'googleAdsSitelinkManagement',
    'googleAdsStructuredSnippetManagement',
    'googleAdsUpdateBudget',
    'googleAdsUpdateKeywords',
    'googleDriveCreateFileFromText',
    'googleDriveCreateFolder',
    'googleDriveDownloadFile',
    'googleDriveFindFile',
    'googleDriveFindFolder',
    'googleDriveGetFileById',
    'googleDriveListFiles',
    'googleDriveMoveFile',
    'googleDriveUploadFile',
    'googleSearchConsoleListSites',
    'googleSearchConsolePerformanceMetrics',
    'googleSearchConsoleUrlInspection',
    'graphicsCreator',
    'graphicsCreatorV2',
    'instagramFetchPosts',
    'instagramFetchProfile',
    'instagramScraper',
    'instagramSearch',
    'linkedInSearchPosts',
    'linkedinCreatePost',
    'linkedinGetAds',
    'linkedinGetCurrentMemberProfile',
    'linkedinGetProfiles',
    'linkedinSearchOrganization',
    'metaAdsFetch',
    'nativeApproval',
    'nativeBackgroundRemoval',
    'nativeBatch',
    'nativeCodeRunner',
    'nativeCsvLlmAction',
    'nativeCsvReader',
    'nativeCsvRowAction',
    'nativeCsvWriter',
    'nativeFileUpload',
    'nativeFilter',
    'nativeFlatten',
    'nativeImageGenerate',
    'nativeLlmResponseText',
    'nativeMerge',
    'nativeRouter',
    'nativeSort',
    'nativeVideoUnderstanding',
    'playwrightGoto',
    'playwrightScreenshot',
    'recentTweets',
    'redditSearch',
    'redditSearchComments',
    'searchTrafficEstimate',
    'seoArticleUpdater',
    'seoArticleWriter',
    'seoBacklinks',
    'seoDomainAnalysis',
    'seoKeywordOpportunityRanker',
    'seoKeywords',
    'seoTechnicalAudit',
    'seoUrlAnalysis',
    'shopifyWebPublisher',
    'sitemapper',
    'twitterPost',
    'webflowActions',
    'wordpressActions',
    'wordpressGetPage',
    'wordpressPublishPage',
    'wordpressUpdatePage',
    'wordpressWebPublisher',
    'workflowWebSearch',
    'youtubeCaptions',
    'youtubeDetails',
    'youtubeSearch',
];
exports.WorkflowToolIds = zod_1.z.enum(exports.WORKFLOW_TOOL_IDS);
// Brand color constants
exports.BRAND_COLORS = {
    // Social Media & Communication
    REDDIT: '#FF4500',
    LINKEDIN: '#0077B5',
    TWITTER: '#1DA1F2',
    INSTAGRAM: '#E1306C',
    META: '#0064e0',
    GMAIL: '#EA4335',
    GOOGLE_DRIVE: '#4285F4',
    GOOGLE_ADS: '#34A853',
    YOUTUBE: '#FF0000',
    WORDPRESS: '#21759b',
    // Search & Web
    EXA: '#1F40ED',
    WEB: '#0EA5E9',
    SEMRUSH: '#00B489',
    // Native/System
    NATIVE: '#6B7280',
    AI: '#8B5CF6',
    APPROVAL: '#F59E0B',
    FILE: '#059669',
    CSV: '#10B981',
    IMAGE: '#EC4899',
    SPRITES: '#6366F1',
    // Playwright/Browser
    BROWSER: '#2D3748',
    // Framer
    FRAMER: '#000000',
    // Webflow
    WEBFLOW: '#4353FF',
    // Shopify
    SHOPIFY: '#96BF48',
};
// Helper function to create background color with opacity
var createBgColor = function (color) { return "".concat(color, "15"); }; // 15 = ~8% opacity in hex
exports.createBgColor = createBgColor;
// Helper functions
var getToolMetadata = function (toolId) {
    return exports.TOOL_DEFINITIONS[toolId];
};
exports.getToolMetadata = getToolMetadata;
var getToolsByCategory = function (category) {
    return Object.values(exports.TOOL_DEFINITIONS).filter(function (tool) { return tool.category === category; });
};
exports.getToolsByCategory = getToolsByCategory;
var getAllToolIds = function () {
    return Object.keys(exports.TOOL_DEFINITIONS);
};
exports.getAllToolIds = getAllToolIds;
var getAllToolMetadata = function (includeAdminOnly) {
    if (includeAdminOnly === void 0) { includeAdminOnly = true; }
    var allTools = Object.values(exports.TOOL_DEFINITIONS);
    if (includeAdminOnly) {
        return allTools;
    }
    return allTools.filter(function (tool) { return !tool.adminOnly; });
};
exports.getAllToolMetadata = getAllToolMetadata;
// Validate tool ID
var isValidToolId = function (id) {
    return id in exports.TOOL_DEFINITIONS;
};
exports.isValidToolId = isValidToolId;
// Validate icon
var isValidIcon = function (icon) {
    return exports.ToolIconSchema.safeParse(icon).success;
};
exports.isValidIcon = isValidIcon;
// Get all available icons
var getAllIcons = function () {
    return exports.ToolIconSchema.options;
};
exports.getAllIcons = getAllIcons;
// Get tools by category as a map
var getToolsByCategoryMap = function () {
    var result = {};
    for (var _i = 0, _a = exports.ToolCategorySchema.options; _i < _a.length; _i++) {
        var category = _a[_i];
        result[category] = (0, exports.getToolsByCategory)(category);
    }
    return result;
};
exports.getToolsByCategoryMap = getToolsByCategoryMap;
// Initialize TOOL_DEFINITIONS - this will be set by the index file
var initializeToolDefinitions = function (definitions) {
    exports.TOOL_DEFINITIONS = definitions;
    // Validate that all tool IDs are present
    var definedIds = Object.keys(definitions).sort();
    var expectedIds = __spreadArray([], exports.WORKFLOW_TOOL_IDS, true).sort();
    if (JSON.stringify(definedIds) !== JSON.stringify(expectedIds)) {
        console.warn('Mismatch between WORKFLOW_TOOL_IDS and TOOL_DEFINITIONS keys:');
        console.warn('Expected:', expectedIds);
        console.warn('Actual:', definedIds);
    }
};
exports.initializeToolDefinitions = initializeToolDefinitions;
// Pipedream node args schema
// Note: Credentials are automatically loaded from workflowState.accountCredentials
function PipedreamNodeArgsSchema(actionId, inputSchema, generateStash) {
    var base = zod_1.z.object({
        actionId: zod_1.z.literal(actionId).describe('The action Id for this tool.'),
        args: inputSchema.describe('The args used to run this actionId'),
        emitMode: exports.emitModeSchema,
    });
    if (generateStash) {
        base = base.extend({
            generateStash: zod_1.z.literal(true),
        });
    }
    return base;
}
function PipedreamNodeResultSchema(responseSchema, exportSchema) {
    return zod_1.z.object({
        response: responseSchema.nullish(),
        exports: exportSchema ? exportSchema.nullish() : zod_1.z.union([zod_1.z.null(), zod_1.z.object({})]),
    });
}
exports.DefaultPipedreamNodeResultSchema = PipedreamNodeResultSchema(zod_1.z.any());
// Google Ads node args schema
function GoogleAdsNodeArgsSchema(inputSchema) {
    return zod_1.z
        .object({
        args: inputSchema.describe('The args used to run this Google Ads API call'),
        emitMode: exports.emitModeSchema.default('single'),
    })
        .describe('Args required to run the Google Ads action. Requires the user to login');
}
function GoogleAdsNodeResultSchema(responseSchema) {
    return zod_1.z.object({
        response: responseSchema.nullish(),
    });
}
exports.RenderingComponentTypeSchema = zod_1.z.enum(['markdown', 'csv', 'html', 'url', 'list']);
exports.RenderingConfigSchema = zod_1.z.object({
    type: exports.RenderingComponentTypeSchema,
    content: zod_1.z.string(),
});
// Common content change schema for incremental content updates (WordPress, Framer, etc.)
exports.contentChangeSchema = zod_1.z.object({
    changeType: zod_1.z
        .enum(['replace', 'insert_after', 'insert_before', 'delete'])
        .describe('replace: Find and replace content. insert_after: Insert content after matched text. insert_before: Insert content before matched text. delete: Remove matched content.'),
    findText: zod_1.z
        .string()
        .describe('Text to find in the content. Should be exact text from the existing content.'),
    replaceWith: zod_1.z
        .string()
        .nullish()
        .describe('New text (for replace) or content to insert (for insert_after/insert_before)'),
    contextBefore: zod_1.z
        .string()
        .nullish()
        .describe('Text appearing before findText, helps if findText appears multiple times'),
    contextAfter: zod_1.z
        .string()
        .nullish()
        .describe('Text appearing after findText, helps if findText appears multiple times'),
});
